"use client";

import { type FormEvent, useEffect, useState } from "react";
import {
  CONTRACT_LABELS,
  REVIEW_CONTRACT_TYPES,
  REVIEW_HONEYPOT_FIELD,
  SALARY_CURRENCIES,
  type FormResult,
} from "@/lib/org-reviews";
import Link from "next/link";
import { buttonClass } from "@/components/ui";
import { createClient } from "@/lib/supabase/client";
import { isSupabaseConfigured } from "@/lib/supabase/env";

/** Signed in? null while loading. Same check as HeaderCta; the server re-checks on submit. */
function useAuthed(): boolean | null {
  const [authed, setAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    // Same guard as HeaderCta: createClient() throws when unconfigured.
    if (!isSupabaseConfigured()) {
      setAuthed(false);
      return;
    }
    const sb = createClient();
    sb.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = sb.auth.onAuthStateChange((_e, s) => setAuthed(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);
  return authed;
}

const input =
  "mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20";
const label = "block text-sm font-medium text-neutral-800";

function Err({ msg }: { msg?: string }) {
  return msg ? <p className="mt-1 text-sm text-red-600">{msg}</p> : null;
}

function useSubmit(action: (fd: FormData) => Promise<FormResult>) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState("busy");
    setErrors({});
    try {
      const res = await action(new FormData(e.currentTarget));
      if (res.ok) return setState("done");
      setErrors(res.errors);
    } catch {
      setErrors({ _form: "Something went wrong — please try again." });
    }
    setState("idle");
  }
  return { errors, state, onSubmit };
}

const Honeypot = () => (
  <input
    type="text"
    name={REVIEW_HONEYPOT_FIELD}
    tabIndex={-1}
    autoComplete="off"
    aria-hidden
    className="absolute -left-[9999px] h-0 w-0"
  />
);

export function ReviewForm({
  orgLabel,
  action,
  cta = "Write a review",
  signUpHref,
}: {
  orgLabel: string;
  action: (fd: FormData) => Promise<FormResult>;
  cta?: string;
  /** Login URL that returns the reader to this tab. Reviews and salaries both need an account. */
  signUpHref: string;
}) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const { errors, state, onSubmit } = useSubmit(action);
  const authed = useAuthed();

  if (authed !== true) {
    return (
      <Link href={signUpHref} rel="nofollow" className={buttonClass("primary", "md")}>
        Sign up to {cta.toLowerCase()}
      </Link>
    );
  }

  if (state === "done") {
    return (
      <div role="status" className="rounded-lg border border-brand-100 bg-brand-50/50 p-4 text-[15px] text-neutral-800">
        Thanks. Your review is in the queue and appears after a quick check, usually within a day.
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={buttonClass("primary", "md")}>
        {cta}
      </button>
    );
  }

  return (
    <form onSubmit={onSubmit} className="relative space-y-4 rounded-lg border border-neutral-200 p-5">
      <Honeypot />
      <div>
        <p className="text-lg font-semibold text-ink">Review {orgLabel}</p>
        <p className="mt-1 text-sm text-neutral-600">
          Anonymous. Nothing that identifies you is stored with it, and {orgLabel} never sees
          who wrote it. Only the month and year are shown.
        </p>
      </div>

      <fieldset>
        <legend className={label}>Overall rating</legend>
        <input type="hidden" name="rating" value={rating || ""} />
        <div className="mt-1 flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              aria-pressed={rating === n}
              onClick={() => setRating(n)}
              className={`text-3xl leading-none ${n <= rating ? "text-brand-600" : "text-neutral-300 hover:text-brand-300"}`}
            >
              ★
            </button>
          ))}
        </div>
        <Err msg={errors.rating} />
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="rv-employment">You are a…</label>
          <select id="rv-employment" name="employment" className={input} defaultValue="current">
            <option value="current">Current employee</option>
            <option value="former">Former employee</option>
          </select>
        </div>
        <div>
          <label className={label} htmlFor="rv-contract">Contract type</label>
          <select id="rv-contract" name="contractType" className={input} defaultValue="">
            <option value="">—</option>
            {REVIEW_CONTRACT_TYPES.map((c) => (
              <option key={c} value={c}>{CONTRACT_LABELS[c]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label} htmlFor="rv-title">Job title</label>
          <input id="rv-title" name="jobTitle" className={input} placeholder="Credit Analyst" />
          <Err msg={errors.jobTitle} />
        </div>
        <div>
          <label className={label} htmlFor="rv-grade">Grade / level <span className="font-normal text-neutral-500">(optional)</span></label>
          <input id="rv-grade" name="grade" className={input} placeholder="Associate, VP, Director…" />
        </div>
        <div className="sm:col-span-2">
          <label className={label} htmlFor="rv-location">Location <span className="font-normal text-neutral-500">(optional)</span></label>
          <input id="rv-location" name="location" className={input} placeholder="London" />
        </div>
      </div>

      <div>
        <label className={label} htmlFor="rv-headline">Headline</label>
        <input id="rv-headline" name="title" className={input} placeholder="Sum it up in a line" />
        <Err msg={errors.title} />
      </div>
      <div>
        <label className={label} htmlFor="rv-pros">Pros</label>
        <textarea id="rv-pros" name="pros" rows={3} className={input} />
        <Err msg={errors.pros} />
      </div>
      <div>
        <label className={label} htmlFor="rv-cons">Cons</label>
        <textarea id="rv-cons" name="cons" rows={3} className={input} />
        <Err msg={errors.cons} />
      </div>

      <fieldset className="rounded-md bg-neutral-50 p-4">
        <legend className="sr-only">Salary</legend>
        <p className={label}>
          Annual base salary <span className="font-normal text-neutral-500">(optional, shown only as part of ranges)</span>
        </p>
        <div className="mt-1 flex gap-2">
          <input name="salaryAmount" inputMode="numeric" className={input} placeholder="85000" />
          <select name="salaryCurrency" className={`${input} max-w-[6.5rem]`} defaultValue="">
            <option value="">—</option>
            {SALARY_CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <Err msg={errors.salaryAmount ?? errors.salaryCurrency} />
      </fieldset>

      <Err msg={errors._form} />
      <div className="flex items-center gap-3">
        <button type="submit" disabled={state === "busy"} className={buttonClass("primary", "md")}>
          {state === "busy" ? "Sending…" : "Submit anonymously"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-sm text-neutral-500 hover:text-ink">
          Cancel
        </button>
      </div>
    </form>
  );
}

export function ClaimForm({
  orgLabel,
  action,
}: {
  orgLabel: string;
  action: (fd: FormData) => Promise<FormResult>;
}) {
  const [open, setOpen] = useState(false);
  const { errors, state, onSubmit } = useSubmit(action);

  if (state === "done") {
    return (
      <p role="status" className="mt-2 text-[13px] text-neutral-700">
        Got it. We&rsquo;ll confirm by email once we&rsquo;ve verified you work at {orgLabel}.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-semibold text-brand-700 underline-offset-2 hover:underline"
      >
        Claim this page →
      </button>
    );
  }
  return (
    <form onSubmit={onSubmit} className="relative mt-3 grid gap-3 sm:grid-cols-3">
      <Honeypot />
      <div>
        <input name="name" aria-label="Your name" placeholder="Your name" className={input} />
        <Err msg={errors.name} />
      </div>
      <div>
        <input name="email" type="email" aria-label="Work email" placeholder="Work email" className={input} />
        <Err msg={errors.email} />
      </div>
      <div>
        <input name="role" aria-label="Your role" placeholder="Role (e.g. HR, Comms)" className={input} />
      </div>
      <div className="sm:col-span-3 flex items-center gap-3">
        <button type="submit" disabled={state === "busy"} className={buttonClass("primary", "sm")}>
          {state === "busy" ? "Sending…" : "Request access"}
        </button>
        <Err msg={errors._form} />
      </div>
    </form>
  );
}
